import { v4 } from "uuid";
import { createServiceBuilder } from "../lib";
import { injectedHistorySlice } from "../reducer";
export { requestNew, requestUpdate, requestDelete, requestComposite } from './requests';
const { actions, selectors } = injectedHistorySlice;
export const ChangeServiceBuilder = createServiceBuilder({
    name: 'ChangeService',
    api: ({ DialogService, NotificationsService, ProjectService }) => {
        const pop = (opts) => (dispatch, getState) => {
            const peeked = selectors.peek(getState(), opts);
            dispatch(actions.dropLatest(opts));
            return peeked;
        };
        const createGroup = (resourceIDs, allowPartialCommit) => ({
            id: v4(),
            allowPartialCommit,
            resourceIDs,
        });
        const getInverseOperation = (op) => (op === 'undo') ? 'redo' : 'undo';
        const getHumanReadableOperation = (op) => (op === 'undo') ? 'Undo' : 'Redo';
        const shouldAskConfirmation = (confirmation, op) => (op === 'undo')
            ? confirmation.onUndo
            : confirmation.onRedo;
        const maybeInferIDFromOrigin = (state, operation, resourceID) => {
            if (resourceID)
                return { id: resourceID, inferredFromOrigin: false };
            const originID = (selectors.getOrigin(state, { origin: 'global' }) || [])
                .map((id) => ({ id, maybeSnapshot: selectors.peek(state, { id, operation }) }))
                .map(({ id, maybeSnapshot }) => ({ id, maybeOrigin: maybeSnapshot?.origin }))
                .filter(({ maybeOrigin }) => maybeOrigin === 'global')
                .at(-1)?.id;
            return originID
                ? { id: originID, inferredFromOrigin: true }
                : { id: undefined, inferredFromOrigin: false };
        };
        const notifyAboutPartialOperation = (op, changeLabel) => NotificationsService.createNotification({
            level: 'warn',
            title: `${getHumanReadableOperation(op)} ${changeLabel} was only partially successful`,
            body: `Some resources could not be updated because they have more recent changes.`
        });
        const notifyAboutFailedOperation = (op, changeLabel) => NotificationsService.createNotification({
            level: 'warn',
            title: `${getHumanReadableOperation(op)} ${changeLabel} was unsuccessful`,
            body: 'Some resources blocked the update because have more recent changes.'
        });
        const getSnapshotsFor = ({ entry, getState, operation }) => {
            if (!entry.group)
                return { committableSnapshots: [entry], partiallyStale: false };
            const candidateResult = entry.group.resourceIDs
                .map((id) => selectors.peek(getState(), { id, operation }))
                .filter((snapshot) => Boolean(snapshot))
                .reduce((acc, snapshot) => ({
                partiallyStale: acc.partiallyStale || (snapshot?.group?.id !== entry.group?.id),
                committableSnapshots: (snapshot?.group?.id === entry.group?.id)
                    ? [...acc.committableSnapshots, snapshot]
                    : acc.committableSnapshots,
            }), { committableSnapshots: [], partiallyStale: false });
            if (candidateResult.partiallyStale && !entry.allowPartialCommit) {
                return { committableSnapshots: [], partiallyStale: true };
            }
            return candidateResult;
        };
        const commitChanges = ({ requests, allowPartialCommit, origin, dispatch, getState }) => {
            const tasks = requests.map((request) => {
                const existingState = (request.type !== 'new') ? ProjectService.getResourceByID(getState(), request.resource.id)
                    : undefined;
                const resource = (request.type === 'new') ? dispatch(ProjectService.createNewResource(request.resourceType))
                    : request.resource;
                const snapshot = {
                    changeLabel: request.changeLabel,
                    resourceID: resource.id,
                    resourceState: existingState,
                    confirmation: request.historyOptions?.confirmation,
                    origin,
                    allowPartialCommit,
                    initialOperation: 'undo',
                };
                return { request, resource, snapshot };
            });
            const resourceIDs = tasks.map(({ resource }) => resource.id);
            const group = createGroup(resourceIDs, allowPartialCommit);
            const results = tasks.map(({ snapshot, ...task }) => ({ ...task, snapshot: { ...snapshot, id: v4(), group } }))
                .map(({ request, resource, snapshot }) => {
                (request.type === 'update') && dispatch(ProjectService.actions.updateResource(request.resource));
                (request.type === 'delete') && dispatch(ProjectService.actions.deleteResource(request.resource.id));
                dispatch(actions.push({ id: resource.id, operation: 'undo', snapshot }));
                dispatch(actions.clear({ id: resource.id, operation: 'redo' }));
                return { id: resource.id, resource: ProjectService.getResourceByID(getState(), resource.id) };
            });
            return {
                resourceIDs,
                first: results.at(0)?.resource,
                byId: results.reduce((acc, { id, resource }) => ({ ...acc, [id]: resource }), {}),
            };
        };
        function commit(req) {
            return async (dispatch, getState) => {
                const { type, historyOptions, origin } = req;
                const isComposite = (type === 'composite');
                const allowPartialCommit = isComposite ? historyOptions?.allowPartialCommit : undefined;
                if (req.historyOptions?.confirmation && req.historyOptions.confirmation.onCommit) {
                    const confirmed = await dispatch(DialogService.createDialog(req.historyOptions.confirmation.message));
                    if (!confirmed)
                        return isComposite ? {} : undefined;
                }
                const { resourceIDs, ...results } = commitChanges({
                    requests: isComposite ? req.requests : [req],
                    allowPartialCommit,
                    origin,
                    dispatch,
                    getState,
                });
                if (origin && resourceIDs) {
                    dispatch(actions.pushOrigin({ origin: 'global', resourceID: resourceIDs[0] }));
                }
                (!origin) && dispatch(actions.dropFromOrigin({ origin: 'global', resourceIDs }));
                return isComposite ? results.byId : results.first;
            };
        }
        ;
        const traverseHistory = async ({ id, operation, dispatch, getState }) => {
            const entry = selectors.peek(getState(), { id, operation });
            const resourceState = ProjectService.getResourceByID(getState(), id);
            if (!entry)
                return resourceState;
            if (entry?.confirmation && shouldAskConfirmation(entry.confirmation, operation)) {
                const confirmed = await dispatch(DialogService.createDialog(entry.confirmation.message));
                if (!confirmed)
                    return resourceState;
            }
            const { committableSnapshots, partiallyStale } = getSnapshotsFor({ entry, operation, getState });
            if (committableSnapshots.length === 0) {
                dispatch(notifyAboutFailedOperation(operation, entry.changeLabel));
                return resourceState;
            }
            partiallyStale && dispatch(notifyAboutPartialOperation(operation, entry.changeLabel));
            (entry.origin) && dispatch(actions.pushOrigin({ origin: entry.origin, resourceID: entry.resourceID }));
            committableSnapshots.forEach((snapshot) => {
                const snapshotResourceState = ProjectService.getResourceByID(getState(), snapshot.resourceID);
                const snapshotToStore = { ...snapshot, id: v4(), resourceState: snapshotResourceState };
                dispatch(pop({ id: snapshot.resourceID, operation }));
                dispatch(actions.push({ id: snapshot.resourceID, operation: getInverseOperation(operation), snapshot: snapshotToStore }));
                dispatch(ProjectService.applySnapshot(snapshot));
            });
            return ProjectService.getResourceByID(getState(), entry.resourceID);
        };
        return {
            commit,
            getRedoSnapshot: (state, resourceID) => {
                const { id } = maybeInferIDFromOrigin(state, 'redo', resourceID);
                return id
                    ? selectors.peek(state, { id, operation: 'redo' })
                    : undefined;
            },
            getUndoSnapshot: (state, resourceID) => {
                const { id } = maybeInferIDFromOrigin(state, 'undo', resourceID);
                return id
                    ? selectors.peek(state, { id, operation: 'undo' })
                    : undefined;
            },
            redo: (resourceID) => async (dispatch, getState) => {
                const { id } = maybeInferIDFromOrigin(getState(), 'redo', resourceID);
                if (!id)
                    return undefined;
                return traverseHistory({ id, operation: 'redo', dispatch, getState });
            },
            undo: (resourceID) => async (dispatch, getState) => {
                const { id } = maybeInferIDFromOrigin(getState(), 'undo', resourceID);
                if (!id)
                    return undefined;
                return traverseHistory({ id, operation: 'undo', dispatch, getState });
            },
        };
    },
});
//# sourceMappingURL=index.js.map